import { LegendConfig } from '../components/charts/MapLegend';
import type { ChoroplethBin } from '../utils/choroplethDesignSystem';
import {
  colorValueForVariable,
  type MapVariableId,
} from '../utils/genericMapClassification';

export interface MapVariableConfig {
  id: string;
  name: string;
  description: string;
  dataSource: string;
  legendConfig: LegendConfig;
  /** Classify using shared bins (from GenericMapTiered). */
  colorFunction: (value: number, bins: ChoroplethBin[]) => string;
}

const colorFor =
  (id: MapVariableId) =>
  (value: number, bins: ChoroplethBin[]): string =>
    colorValueForVariable(id, value, bins);

export const incomeMapConfig: MapVariableConfig = {
  id: 'income',
  name: 'Median Household Income',
  description: 'Median household income by census tract',
  dataSource: 'acs5_income',
  legendConfig: {
    title: 'Median Household Income',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: '$',
    tractCount: 0,
    // Static reference — TODO: wire live statewide median when available in this data path
    referenceValue: { label: 'California Median (static ref.)', value: 95521 },
  },
  colorFunction: colorFor('income'),
};

export const totalEvsConfig: MapVariableConfig = {
  id: 'total_evs',
  name: 'Total Electric Vehicles',
  description: 'Estimated plug-in electric vehicles by census tract (rate × population)',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Total Electric Vehicles',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: 'EVs',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 85 },
  },
  colorFunction: colorFor('total_evs'),
};

export const evAdoptionConfig: MapVariableConfig = {
  id: 'ev_adoption',
  name: 'Electric Vehicles per 100 People',
  description: 'Number of electric vehicles per 100 people by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Electric Vehicles per 100 People',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 2.1 },
  },
  colorFunction: colorFor('ev_adoption'),
};

export const vehiclesPerHouseholdConfig: MapVariableConfig = {
  id: 'vehicles_per_household',
  name: 'Vehicles per Household',
  description: 'Average number of vehicles per household by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Vehicles per Household',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 2.1 },
  },
  colorFunction: colorFor('vehicles_per_household'),
};

export const totalChargersConfig: MapVariableConfig = {
  id: 'total_chargers',
  name: 'Total EV Chargers',
  description: 'Total number of EV chargers by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Total EV Chargers',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: 'count',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 5.2 },
  },
  colorFunction: colorFor('total_chargers'),
};

export const populationDensityConfig: MapVariableConfig = {
  id: 'population_density',
  name: 'Population Density',
  description: 'Population density in persons per square mile by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Population Density',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: ' per sq mi',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 2500 },
  },
  colorFunction: colorFor('population_density'),
};

export const politicalLeanConfig: MapVariableConfig = {
  id: 'political_lean',
  name: 'Political Lean',
  description: 'Democratic minus Republican voter percentage by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Political Lean',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average (static ref.)', value: 15 },
  },
  colorFunction: colorFor('political_lean'),
};

export const carbonFootprintConfig: MapVariableConfig = {
  id: 'carbon_footprint',
  name: 'Average Household Carbon Footprint',
  description: 'Average household carbon footprint by census tract',
  dataSource: 'cbei_tracts',
  legendConfig: {
    title: 'Average Household Carbon Footprint',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: ' MT CO₂e/HH',
    tractCount: 0,
    referenceValue: { label: 'CA Average (static ref.)', value: 45.2 },
  },
  colorFunction: colorFor('carbon_footprint'),
};

export const commuteTimeConfig: MapVariableConfig = {
  id: 'commute_time',
  name: 'Average Commute Time',
  description: 'Average travel time to work in minutes by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Average Commute Time',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: ' min',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 28.5 },
  },
  colorFunction: colorFor('commute_time'),
};

export const zeroVehicleHouseholdsConfig: MapVariableConfig = {
  id: 'zero_vehicle_households',
  name: 'Zero Vehicle Households',
  description: 'Share of households with no vehicle available (ACS-derived tract percent).',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Zero Vehicle Households',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: ' households',
    tractCount: 0,
    referenceValue: { label: 'California mean per tract (static ref., 2023)', value: 150 },
  },
  colorFunction: colorFor('zero_vehicle_households'),
};

export const ownerOccupiedHousingConfig: MapVariableConfig = {
  id: 'owner_occupied_housing',
  name: 'Owner-Occupied Housing',
  description: 'Percentage of owner-occupied housing units by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Owner-Occupied Housing',
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: '%',
    tractCount: 0,
    referenceValue: { label: 'California Average (static ref.)', value: 55.3 },
  },
  colorFunction: colorFor('owner_occupied_housing'),
};

export const educationalAttainmentConfig: MapVariableConfig = {
  id: 'educational_attainment',
  name: "Bachelor's Degree or Higher",
  description: "Percentage of adults 25+ with bachelor's degree or higher by census tract",
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: "Bachelor's Degree or Higher",
    dataYear: '2023 Data',
    colorScale: [],
    breaks: [],
    unit: '%',
    tractCount: 0,
    referenceValue: { label: 'California Average (static ref.)', value: 32.4 },
  },
  colorFunction: colorFor('educational_attainment'),
};

export const mapConfigs: MapVariableConfig[] = [
  commuteTimeConfig,
  evAdoptionConfig,
  totalEvsConfig,
  incomeMapConfig,
  populationDensityConfig,
  totalChargersConfig,
  vehiclesPerHouseholdConfig,
  zeroVehicleHouseholdsConfig,
];

export const getMapConfig = (id: string): MapVariableConfig | undefined => {
  const configFromArray = mapConfigs.find((config) => config.id === id);
  if (configFromArray) return configFromArray;
  if (id === 'political_lean') return politicalLeanConfig;
  if (id === 'carbon_footprint') return carbonFootprintConfig;
  if (id === 'owner_occupied_housing') return ownerOccupiedHousingConfig;
  if (id === 'educational_attainment') return educationalAttainmentConfig;
  return undefined;
};
